import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { FolderZipOutlined } from "@mui/icons-material";
import axiosInstant from "../../api";
import { MainContainer } from "../ContentWrapper";
import { ListPageHeader } from "../ListPageHeader/ListPageHeader";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { DataTable, DataTableColumn } from "../DataTable/DataTable";
import { RequestedByCell } from "../RequestedByCell";
import { useCommonParams } from "../../hooks/useCommonParams";
import { useResourceList } from "../../hooks/useResourceList";
import { useBrandDetailLink } from "../../hooks/useBrandDetailLink";
import { IconTrash, IconPlus, IconRefresh } from "../../utils/icons";
import { initialsFromName } from "../../utils";
import { useProductDetailLink } from "../../hooks/useProductDetailLink";
import { downloadBlob } from "../PdpAudit/reportFiles";
import { buildPrepPackZip, prepPackFileBase } from "./prepPackFiles";
import PrepPackForm from "../../pages/sales/prepPacks/PrepPackForm";
import { PrepPack, isV2Report } from "../../pages/sales/prepPacks/types";
import { GENERATING_STEPS, STAGE_TO_STEP, STAGE_SHORT_LABELS } from "../../pages/sales/prepPacks/constants";

// Reusable prep-pack list + create/regenerate/download surface, extracted
// from the Sales page (SalesAdmin, Brand Intelligence tab) so the RMN white-label
// portal can run the same tool against the rmnControl-scoped
// /rmn/prep-packs endpoints. The internal sales page keeps its exact
// behavior via the admin endpoints (allowDelete, open brand search).

const PAGE_SIZE = 25;

// Rows stuck 'generating' are re-fetched on this cadence until they settle.
const GENERATING_POLL_MS = 8000;

const formatDate = (iso: string) => (iso ? dayjs(iso).format("MMM DD, YYYY h:mm A") : "—");

// Packs are named by their product focus ("Brand — Product focus"); older
// rows fall back to the plain brand_name, then the joined catalog brand.
const brandLabel = (p: PrepPack) => p.title || p.brand_name || p.brand?.name || "—";

const urlHostname = (url: string) => {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
};

// QA-verdict chip for ready v2 packs (internal surface only — the RMN
// white-label list hides pipeline diagnostics from brand users).
// Light keeps the original pastel washes; dark falls back to the theme's own
// status/gold pairs, which are translucent and readable on the dark canvas.
const QA_CHIP_STYLES: Record<string, string> = {
    PASS: "bg-[#E7F4EE] text-[#1C7A5A] dark:bg-up-bg dark:text-up",
    REVIEW: "bg-[#FAF0DC] text-[#A07B2E] dark:bg-tint dark:text-accent-text",
    FAIL: "bg-[#FCEBEB] text-[#A32D2D] dark:bg-down-bg dark:text-down",
};

function QaBadge({ pack }: { pack: PrepPack }) {
    if (!isV2Report(pack.report)) return null;
    const { qa, warnings, parentCorrection } = pack.report;
    return (
        <>
            {qa ? (
                <span
                    className={`text-[10px] font-bold px-[6px] py-[1px] rounded ${QA_CHIP_STYLES[qa.verdict] || QA_CHIP_STYLES.REVIEW}`}
                    title="QA gate verdict — full findings ship as qa-findings.txt in the download"
                >
                    QA {qa.verdict}
                </span>
            ) : null}
            {warnings && warnings.length > 0 ? (
                <span
                    className="text-[11px] font-bold text-accent-text cursor-default"
                    title={`Warnings: ${warnings.join(", ")}`}
                >
                    !
                </span>
            ) : null}
            {parentCorrection?.detected ? (
                <span
                    className="text-[10px] font-semibold text-[#3C3489] dark:text-[#BCB7F0] whitespace-nowrap"
                    title={parentCorrection.reason || "Parent company corrected from the intake input"}
                >
                    → {parentCorrection.corrected}
                </span>
            ) : null}
        </>
    );
}

// Exported: the internal Brands card's BI row shows the same badge for the
// brand's latest pack, so "generating" reads identically on both surfaces.
// `pack` is the fields the badge reads — a brand row's summary
// ({id, status, created_at}) qualifies, not only a full PrepPack.
export type PrepPackStatusSource = Pick<PrepPack, "status"> &
    Partial<Pick<PrepPack, "stage" | "llm_error" | "gemini_error">>;

export function PrepPackStatusBadge({
    pack,
    regenerating,
    internal,
}: {
    pack: PrepPackStatusSource;
    regenerating: boolean;
    internal: boolean;
}) {
    if (regenerating) {
        return (
            <span className="inline-flex items-center gap-[6px] text-[11.5px] font-semibold text-accent-text">
                <span className="w-3 h-3 border-2 border-tint border-t-accent rounded-full animate-ppSpin" /> Regenerating
            </span>
        );
    }
    if (pack.status === "ready") {
        return (
            <span className="inline-flex items-center gap-[6px] text-[11.5px] font-semibold text-[#1C7A5A] dark:text-up">
                <span className="w-[7px] h-[7px] rounded-full bg-[#1C7A5A] dark:bg-up" /> Ready
                {internal && "qa" in pack ? <QaBadge pack={pack as unknown as PrepPack} /> : null}
            </span>
        );
    }
    if (pack.status === "error") {
        return (
            <span
                className="inline-flex items-center gap-[6px] text-[11.5px] font-semibold text-[#A32D2D] dark:text-down"
                title={pack.llm_error || pack.gemini_error || "Generation failed"}
            >
                Error
            </span>
        );
    }
    // Generating: surface the live pipeline stage when the server reports it
    // — since creation runs fully in the background now, this badge is the
    // only progress indicator.
    const stepIdx = pack.stage ? STAGE_TO_STEP[pack.stage] : undefined;
    const stageLabel = stepIdx !== undefined ? GENERATING_STEPS[stepIdx] : undefined;
    const shortLabel = pack.stage ? STAGE_SHORT_LABELS[pack.stage] : undefined;
    return (
        <span
            className="inline-flex items-center gap-[6px] text-[11.5px] font-semibold text-accent-text"
            title={stageLabel}
        >
            <span className="w-3 h-3 border-2 border-tint border-t-accent rounded-full animate-ppSpin" /> Generating
            {shortLabel ? <span className="font-normal text-accent-text">· {shortLabel}</span> : null}
        </span>
    );
}

export interface PrepPackListProps {
    endpoints: {
        list: string;
        item: (id: number) => string;
        regenerate: (id: number) => string;
    };
    allowDelete: boolean;
    productsNavigable?: boolean;
    // Internal sales surface shows pipeline diagnostics (QA verdict,
    // warnings, qa-findings.txt in the ZIP); the RMN white-label surface
    // leaves this false so brand users never see them.
    internal?: boolean;
    // The ad-hoc "New Sales Intel Prep Pack" form (any brand
    // name + product URLs, catalog or not). On for the internal sales page
    // and the network (Enterprise RMN / Agency) brand-client page.
    allowCreate?: boolean;
    // Rendered as a tab of the Sales page: no page shell (the parent owns
    // the header, search box and tab strip, and reads the same URL params),
    // the create form opens from the parent's header action, and the row
    // count feeds the tab label.
    embedded?: {
        modalOpen: boolean;
        onCloseModal: () => void;
        onCountChange?: (count: number) => void;
    };
}

export default function PrepPackList({
    endpoints,
    allowDelete,
    productsNavigable = false,
    internal = false,
    allowCreate = true,
    embedded,
}: PrepPackListProps) {
    const { goToProduct } = useProductDetailLink();
    const { enabled: brandLinkEnabled, goToBrand } = useBrandDetailLink();
    const { searchParams, setSearchParams, searchText, setSearchText, page, setPage } = useCommonParams();
    const [searchInput, setSearchInput] = useState(searchText);
    const [count, setCount] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<PrepPack | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [pdfLoadingId, setPdfLoadingId] = useState<number | null>(null);
    const [regenLoadingId, setRegenLoadingId] = useState<number | null>(null);

    useEffect(() => {
        if (searchInput === searchText) return;
        const t = setTimeout(() => setSearchText(searchInput), 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    useEffect(() => {
        if (searchText !== searchInput) setSearchInput(searchText);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText]);

    const list = useResourceList<PrepPack>({
        endpoint: endpoints.list,
        pageSize: PAGE_SIZE,
        errorLabel: "Failed to load Sales Intel Prep Packs",
        searchText,
        searchParams,
        setSearchParams,
        page,
        setPage,
        onCountChange: setCount,
    });

    const onEmbeddedCount = embedded && embedded.onCountChange;
    useEffect(() => {
        if (onEmbeddedCount) onEmbeddedCount(count);
    }, [count, onEmbeddedCount]);

    // Background polling: while any visible row is still generating (create
    // and regenerate both return immediately now), refresh the loaded pages
    // without a loading flicker until every row settles.
    const hasGenerating = list.items.some((r) => r.status === "generating");
    useEffect(() => {
        if (!hasGenerating) return;
        const t = setInterval(() => {
            list.silentRefresh();
        }, GENERATING_POLL_MS);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasGenerating, list.silentRefresh]);

    const handleDownload = async (pack: PrepPack) => {
        const brand = brandLabel(pack);
        if (!pack.report) {
            toast.error("The prep pack is still generating — try downloading once it's ready.");
            return;
        }
        setPdfLoadingId(pack.id);
        try {
            // The list payload omits the audits (heavy JSONB) — the detail
            // GET carries them for the pdp-audits/ folder of the ZIP.
            const { data: full } = await axiosInstant.get<PrepPack>(
                endpoints.item(pack.id),
            );
            const blob = await buildPrepPackZip(full, { internal });
            downloadBlob(blob, `${prepPackFileBase(full)}.zip`);
            toast.success(`Downloaded the ${brand} prep pack`);
        } catch {
            toast.error(`Couldn't build the download for ${brand}. Please try again.`);
        } finally {
            setPdfLoadingId(null);
        }
    };

    const handleRegenerate = async (pack: PrepPack) => {
        const brand = brandLabel(pack);
        setRegenLoadingId(pack.id);
        try {
            await axiosInstant.post<PrepPack>(endpoints.regenerate(pack.id));
            // Regeneration runs in the background; the generating badge +
            // list polling take it from here.
            await list.silentRefresh();
            toast.success(`Regenerating the ${brand} prep pack — it refreshes automatically when done.`);
        } catch (e: any) {
            const msg = e?.response?.data?.error;
            toast.error(
                typeof msg === "string" ? msg : `Couldn't regenerate the ${brand} prep pack. Please try again.`,
            );
        } finally {
            setRegenLoadingId(null);
        }
    };

    const handleDelete = async () => {
        if (!pendingDelete) return;
        const brand = brandLabel(pendingDelete);
        setDeleting(true);
        try {
            await axiosInstant.delete(endpoints.item(pendingDelete.id));
            toast.success(`Deleted the ${brand} prep pack`);
            setPendingDelete(null);
            await list.initLoad(1);
        } catch {
            toast.error(`Couldn't delete the ${brand} prep pack. Please try again.`);
        } finally {
            setDeleting(false);
        }
    };

    const columns: DataTableColumn<PrepPack>[] = [
        {
            key: "brand",
            header: "Prep Pack",
            render: (p) => {
                const canClick = brandLinkEnabled && p.brand?.id != null;
                return (
                    <div
                        className={`flex items-center gap-[10px] font-semibold text-text-1${
                            canClick ? " cursor-pointer hover:text-accent-text" : ""
                        }`}
                        onClick={
                            canClick
                                ? (e) => {
                                      e.stopPropagation();
                                      goToBrand(p.brand?.id);
                                  }
                                : undefined
                        }
                    >
                        <div className="w-[30px] h-[30px] rounded-[7px] bg-tint text-accent-text text-[11px] font-bold flex items-center justify-center shrink-0 overflow-hidden">
                            {p.brand?.logo ? (
                                <img src={p.brand.logo} alt="" className="w-full h-full object-cover" />
                            ) : (
                                initialsFromName(brandLabel(p))
                            )}
                        </div>
                        {brandLabel(p)}
                    </div>
                );
            },
        },
        {
            key: "products",
            header: "Products",
            render: (p) => {
                // New packs list audited product URLs; legacy packs keep
                // their catalog product titles.
                const chips = p.product_urls?.length
                    ? p.product_urls.map((url) => ({
                          key: url,
                          label: urlHostname(url),
                          title: url,
                          desireid: undefined as number | undefined,
                      }))
                    : p.products.map((pr) => ({
                          key: String(pr.desireid),
                          label: pr.title,
                          title: pr.title,
                          desireid: pr.desireid as number | undefined,
                      }));
                return (
                    <div className="flex items-center gap-[5px] flex-wrap">
                        {chips.slice(0, 2).map((c) => {
                            const canClick =
                                productsNavigable && c.desireid != null && p.brand_id != null;
                            return (
                                <span
                                    key={c.key}
                                    onClick={
                                        canClick
                                            ? (e) => {
                                                  e.stopPropagation();
                                                  goToProduct({ desireid: c.desireid, brand_id: p.brand_id });
                                              }
                                            : undefined
                                    }
                                    className={`text-[11px] bg-[#EEEDFE] text-[#3C3489] dark:bg-[#3C3489]/35 dark:text-[#BCB7F0] px-2 py-[2px] rounded-md font-medium whitespace-nowrap max-w-[140px] overflow-hidden text-ellipsis${
                                        canClick ? " cursor-pointer hover:bg-[#E0DEFB] dark:hover:bg-[#3C3489]/50" : ""
                                    }`}
                                    title={c.title}
                                >
                                    {c.label}
                                </span>
                            );
                        })}
                        {chips.length > 2 && (
                            <span className="text-[11px] text-muted font-semibold">+{chips.length - 2}</span>
                        )}
                    </div>
                );
            },
        },
        {
            key: "category",
            header: "Category",
            render: (p) => <span className="text-text-2">{p.category || "—"}</span>,
        },
        // Internal only: who requested the pack. Brand-portal lists stay as
        // they are (their viewer is the brand).
        ...(internal
            ? [
                  {
                      key: "requested_by",
                      header: "Requested by",
                      render: (p: PrepPack) => <RequestedByCell user={p.user} />,
                  } as DataTableColumn<PrepPack>,
              ]
            : []),
        {
            key: "status",
            header: "Status",
            render: (p) => (
                <PrepPackStatusBadge pack={p} regenerating={regenLoadingId === p.id} internal={internal} />
            ),
        },
        {
            key: "created",
            header: "Created",
            render: (p) => formatDate(p.created_at),
            className: "text-muted text-[12px] font-mono",
        },
        {
            key: "actions",
            header: "",
            width: allowDelete ? 170 : 130,
            render: (p) => {
                const isRegen = regenLoadingId === p.id;
                return (
                    <div className="flex items-center gap-1 justify-end">
                        <button
                            type="button"
                            title="Download report (ZIP with PDP audits)"
                            disabled={p.status !== "ready" || pdfLoadingId === p.id || isRegen}
                            onClick={() => handleDownload(p)}
                            aria-label="Download ZIP"
                            className="w-8 h-8 rounded-md text-muted flex items-center justify-center cursor-pointer hover:text-text-1 hover:bg-surface-2 disabled:opacity-40 disabled:cursor-default"
                        >
                            <FolderZipOutlined sx={{ fontSize: 16 }} />
                        </button>
                        <button
                            type="button"
                            title={isRegen ? "Regenerating…" : "Regenerate"}
                            disabled={isRegen}
                            onClick={() => handleRegenerate(p)}
                            className="w-8 h-8 rounded-md text-text-2 flex items-center justify-center cursor-pointer hover:bg-surface-2 disabled:opacity-40 disabled:cursor-default"
                        >
                            {isRegen ? <span className="w-3 h-3 border-2 border-tint border-t-accent rounded-full animate-ppSpin" /> : <IconRefresh />}
                        </button>
                        {allowDelete && (
                            <button
                                type="button"
                                title="Delete"
                                disabled={isRegen}
                                onClick={() => setPendingDelete(p)}
                                className="w-8 h-8 rounded-md text-muted flex items-center justify-center cursor-pointer hover:text-down hover:bg-down-bg disabled:opacity-40 disabled:cursor-default"
                            >
                                <IconTrash width={16} height={16} stroke="currentColor" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const formOpen = embedded ? embedded.modalOpen : modalOpen;
    const closeForm = embedded ? embedded.onCloseModal : () => setModalOpen(false);

    const body = (
        <>
            <DataTable<PrepPack>
                columns={columns}
                rows={list.items}
                loading={list.loading}
                totalCount={list.totalCount}
                hasMore={list.hasMore}
                error={list.error}
                onLoadMore={list.loadMore}
                emptyMessage="No Sales Intel Prep Packs found"
                getRowKey={(r) => r.id}
            />

            {formOpen && (
                <PrepPackForm
                    createEndpoint={endpoints.list}
                    onClose={closeForm}
                    onCreated={async () => {
                        // The create POST returns a 'generating' pack — the
                        // generating badge + list polling take it from here.
                        closeForm();
                        await list.initLoad(1);
                    }}
                />
            )}

            {pendingDelete && (
                <ConfirmationDialog
                    open
                    title="Delete Sales Intel Prep Pack"
                    description={`Delete the prep pack "${brandLabel(pendingDelete)}"? This cannot be undone.`}
                    confirmLabel="Delete"
                    onClose={() => setPendingDelete(null)}
                    onConfirm={handleDelete}
                    disabled={deleting}
                />
            )}
        </>
    );

    if (embedded) return body;

    return (
        <MainContainer>
            <ListPageHeader
                title="Sales Intel Prep Pack"
                subtitle={
                    count > 0
                        ? `Brand intelligence, PDP and Consumer Voice analysis per product · ${count} total`
                        : "Brand intelligence, PDP and Consumer Voice analysis per product"
                }
                actionLabel={allowCreate ? "Create New Brand Intelligence Report" : undefined}
                onActionClick={allowCreate ? () => setModalOpen(true) : undefined}
                actionIcon={allowCreate ? <IconPlus width={16} height={16} stroke="#FFF" /> : undefined}
                searchValue={searchInput}
                onSearchChange={setSearchInput}
                searchInputId="search-prep-packs"
                searchPlaceholder="Search by brand…"
            />
            {body}
        </MainContainer>
    );
}
